import React, { useRef, useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { ImageNavigation } from '@/components/navigation';
import { BlurUpImage } from './BlurUpImage';
import 'swiper/css';
import 'swiper/css/navigation';

interface ImageCarouselProps {
  images: string[];
  alt?: string;
  height?: string | { base?: string; lg?: string };
  currentIndex?: number;
  onIndexChange?: (index: number) => void;
  fallbackContent?: React.ReactNode;
  /** Render the first slide without the lazy opacity fade (see `BlurUpImage`'s `eager`). */
  eagerFirstImage?: boolean;
  /** Don't render the built-in arrows/indicator — the caller renders `ImageNavigation` itself (e.g. in a layer above hero overlays). Pair with controlled `currentIndex`/`onIndexChange`. */
  hideNavigation?: boolean;
}

export const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  alt = 'Image',
  height = '200px',
  currentIndex: controlledIndex,
  onIndexChange,
  fallbackContent,
  eagerFirstImage = false,
  hideNavigation = false,
}) => {
  const swiperRef = useRef<any>(null);
  const [internalIndex, setInternalIndex] = React.useState(0);

  // Use controlled index if provided, otherwise use internal state
  const currentIndex = controlledIndex ?? internalIndex;
  const setCurrentIndex = onIndexChange ?? setInternalIndex;

  // Sync swiper position when controlled index changes
  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(currentIndex);
    }
  }, [currentIndex]);

  if (!images || images.length === 0) {
    if (fallbackContent) {
      return <>{fallbackContent}</>;
    }
    return null;
  }

  return (
    <Box position="relative" flexShrink={0} h={height} w="100%">
      <Swiper
        modules={[Navigation]}
        navigation={false}
        onSlideChange={(swiper) => setCurrentIndex(swiper.activeIndex)}
        onSwiper={(swiper) => swiperRef.current = swiper}
        initialSlide={currentIndex}
        style={{ height: '100%', width: '100%' }}
      >
        {images.map((image, index) => (
          <SwiperSlide key={index}>
            <BlurUpImage
              src={image}
              alt={`${alt} ${index + 1}`}
              height="100%"
              width="100%"
              objectFit="cover"
              eager={eagerFirstImage && index === 0}
            />
          </SwiperSlide>
        ))}
      </Swiper>
      {!hideNavigation && (
        <ImageNavigation
          currentIndex={currentIndex}
          totalImages={images.length}
          onPrevious={() => {
            const newIndex = Math.max(0, currentIndex - 1);
            setCurrentIndex(newIndex);
          }}
          onNext={() => {
            const newIndex = Math.min(images.length - 1, currentIndex + 1);
            setCurrentIndex(newIndex);
          }}
          preventPropagation={true}
        />
      )}
    </Box>
  );
};

export default ImageCarousel;
